import { useMemo } from "react";
import { useTranslation } from "react-i18next";

/**
 * PhaseStrip — Athlete Plan macrocycle timeline.
 *
 * Caller hands us:
 *   - phases: ordered phase descriptors with name + start/end ISO dates,
 *   - todayISO: anchor date so the "current" highlight is testable
 *     without Date.now(),
 *   - optional raceDateISO: a race phase appears as an extra rightmost
 *     marker.
 *
 * Each phase paints in one of four tones depending on where today
 * sits in its date range:
 *   past    — fully behind today
 *   current — today inside [start, end]
 *   future  — start > today
 *   race    — race day pinned on the rightmost edge (different visual)
 *
 * Pure presentational atom — no hooks beyond useMemo for the tone
 * decision. The page-level wrapper fetches phases from /athlete/plan
 * and the race date from /athlete/dashboard.goal.
 */

export type PhaseTone = "past" | "current" | "future" | "race";

/** Macrocycle phase type — recognised by name regardless of language. */
export type PhaseType = "base" | "build" | "peak" | "taper" | "recovery" | "race";

export interface PhaseSpec {
  name: string;
  start: string;   // ISO YYYY-MM-DD
  end: string;     // ISO YYYY-MM-DD
  // Phase 5 (design-recompose) — optional CTL guidance for this phase.
  // Backward compatible: existing callers that omit it render unchanged.
  ctlTarget?: number | null;
}

export interface PhaseStripProps {
  phases: PhaseSpec[];
  todayISO: string;
  raceDateISO?: string | null;
  raceLabel?: string;
  totalWeeksLabel?: string;
  className?: string;
}

const TONE_BG: Record<PhaseTone, string> = {
  past: "bg-bg-raised",
  current: "bg-accent/15 border-accent/40",
  future: "bg-bg-surface",
  race: "bg-ok/15 border-ok/40",
};

const TONE_LABEL: Record<PhaseTone, string> = {
  past: "text-fg-secondary",
  current: "text-accent",
  future: "text-fg-muted",
  race: "text-ok",
};

// P0.2 P7 — phase-type ribbon (5-color subtone, left 3px bar). Pure
// presentational accent on top of the temporal background so the canon
// "Base / Build / Peak / Taper / Race" sequence reads at a glance.
const PHASE_TYPE_BAR: Record<PhaseType, string> = {
  base:     "bg-disc-swim",   // tealish — endurance foundation
  build:    "bg-warn",        // amber — work threshold
  peak:     "bg-bad",         // red — race-specific intensity
  taper:    "bg-accent",      // indigo — recovery before race
  recovery: "bg-ok",          // green — post-race
  race:     "bg-ok",
};

export function pickPhaseType(name: string): PhaseType {
  const n = name.trim().toLowerCase();
  // Russian / Ukrainian / German / English canon names.
  if (/(база|базов|base|grundlag)/.test(n)) return "base";
  if (/(развит|build|aufbau)/.test(n)) return "build";
  if (/(пик|peak|spitze)/.test(n)) return "peak";
  if (/(подвод|taper|tapering|abklingen)/.test(n)) return "taper";
  if (/(восстан|recovery|erholung)/.test(n)) return "recovery";
  if (/(гонк|race|wettkampf|rennen)/.test(n)) return "race";
  return "build"; // safe default for unknown labels
}

function compareISO(a: string, b: string): number {
  return a.localeCompare(b);
}

export function pickPhaseTone(phase: PhaseSpec, today: string): PhaseTone {
  // race tone is handled at the strip level via a separate marker;
  // here we only return past/current/future for date-range phases.
  if (compareISO(phase.end, today) < 0) return "past";
  if (compareISO(phase.start, today) > 0) return "future";
  return "current";
}

function formatRange(start: string, end: string): string {
  // "DD.MM–DD.MM" so the strip reads compact at 7-card width
  const fmt = (iso: string) => {
    const [, m, d] = iso.split("-");
    return `${d}.${m}`;
  };
  return `${fmt(start)}–${fmt(end)}`;
}

function formatSingle(iso: string): string {
  const [, m, d] = iso.split("-");
  return `${d}.${m}`;
}

export function PhaseStrip({
  phases,
  todayISO,
  raceDateISO,
  raceLabel,
  totalWeeksLabel,
  className = "",
}: PhaseStripProps) {
  const { t } = useTranslation("plan");

  const decorated = useMemo(
    () => phases.map((p) => ({
      ...p,
      tone: pickPhaseTone(p, todayISO),
      type: pickPhaseType(p.name),
    })),
    [phases, todayISO],
  );

  if (phases.length === 0 && !raceDateISO) {
    return (
      <div
        data-testid="phase-strip-empty"
        className={`indigo-card p-4 ${className}`}
      >
        <div className="text-[9.5px] uppercase tracking-[0.22em] text-fg-muted font-bold">
          {t("phase_strip.title", { defaultValue: "Макроцикл" })}
        </div>
        <div className="mt-2 text-xs text-fg-muted">
          {t("phase_strip.empty", { defaultValue: "Фаз ещё нет — тренер соберёт план" })}
        </div>
      </div>
    );
  }

  return (
    <div
      data-testid="phase-strip"
      className={`indigo-card p-4 flex flex-col gap-3 ${className}`}
    >
      <div className="flex items-center gap-2.5">
        <span className="w-[3px] h-3 rounded-sm bg-accent inline-block" />
        <span className="text-[9.5px] uppercase tracking-[0.22em] text-fg-muted font-bold">
          {totalWeeksLabel
            ? `${t("phase_strip.title", { defaultValue: "Макроцикл" })} · ${totalWeeksLabel}`
            : t("phase_strip.title", { defaultValue: "Макроцикл" })}
        </span>
      </div>

      <div className="flex gap-1.5">
        {decorated.map((p, i) => (
          <div
            key={`${p.name}-${i}`}
            data-testid={`phase-strip-item-${i}`}
            data-tone={p.tone}
            data-phase-type={p.type}
            className={`flex-1 px-3 py-2.5 rounded-md border border-bg-border ${TONE_BG[p.tone]} relative overflow-hidden`}
          >
            <span
              aria-hidden
              className={`absolute left-0 top-0 bottom-0 w-[3px] ${PHASE_TYPE_BAR[p.type]}`}
            />
            <div className={`pl-1.5 text-[10px] uppercase tracking-[0.18em] font-bold ${TONE_LABEL[p.tone]}`}>
              {p.name}
            </div>
            <div className="pl-1.5 mt-1 text-[8.5px] uppercase tracking-[0.1em] text-fg-muted font-semibold tabular-nums">
              {formatRange(p.start, p.end)}
            </div>
            {p.ctlTarget != null && (
              <div
                data-testid="phase-strip-ctl-target"
                className="pl-1.5 mt-0.5 text-[8.5px] text-fg-muted/80 tabular-nums"
              >
                CTL {p.ctlTarget.toFixed(0)}
              </div>
            )}
          </div>
        ))}
        {raceDateISO && (
          <div
            data-testid="phase-strip-race"
            data-tone="race"
            className={`px-3 py-2.5 rounded-md border ${TONE_BG.race} min-w-[88px]`}
          >
            <div className={`text-[10px] uppercase tracking-[0.18em] font-bold ${TONE_LABEL.race}`}>
              {raceLabel ?? t("phase_strip.race", { defaultValue: "ГОНКА" })}
            </div>
            <div className="mt-1 text-[8.5px] uppercase tracking-[0.1em] text-fg-muted font-semibold tabular-nums">
              {formatSingle(raceDateISO)}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
